// @flow
import appConfig from "appConfig";
import getLogger from "common/logger";
import { fileSelect } from "tabs/common/actionMethods";
import { selectListRequestId } from "tabs/GoogleDrive/selectors/navigationSelectors";
import createBoundActionCreators, {
	createCustomCreator,
	createDispatcher,
	generateApiActionType,
} from "common/actionsBase";
import rootBindActions, { addNotification, types as rootTypes } from "root/actions";
import { selectIsCropEnabled } from "root/selectors";
import NOTIFICATIONS, { NOTIFICATION_IDS } from "../data/notifications";
import { ORDER_BY, SEARCH_RESULTS_ID, SORT } from "../data/consts";
import {
	selectIsIdSelected,
	selectSelectedIds,
	selectReachedMaxSelection,
	selectCurrentContext,
	selectMultipleIdsInRange,
	selectAvailableUntilMax,
} from "../selectors";
import { selectFiles } from "../selectors/navigationSelectors";
import getGDrive from "../service";
import type { ServiceOptions } from "../service";
import { getFiles } from "./actionsHelpers";


const driveConf = appConfig.google.drive;

const logger = getLogger();

export const types = {
	INIT_AUTH: "GD_INIT_AUTH",
	DO_LOGIN: "GD_DO_LOGIN",
	DO_LOGOUT: "GD_DO_LOGOUT",
	SET_AUTH_START: "GD_SET_AUTH_START",
	SET_AUTH_INFO: "GD_SET_AUTH_INFO",
	SET_FILE_SELECTION: "GD_SET_FILE_SELECTION",
	SET_MULTI_FILE_SELECTION: "GD_SET_MUTLI_FILE_SELECTION",
	SET_USER_LOGOUT_SUCCESS: "GD_SET_USER_LOGOUT_SUCCESS",
	CLEAR_IMAGE_SELECTION: "GD_CLEAR_IMAGE_SELECTION",
	TOGGLE_PANEL: "GD_TOGGLE_PANEL",
	ADD_ITEM: "GD_ADD_ITEM",
	ADD_CHILDREN_TO_ITEM: "GD_ADD_CHILDREN_TO_ITEM",
	GET_FOLDERS_BY_PARENT_ID: "GD_GET_FOLDERS_BY_PARENT_ID",
	GET_SHARED_DRIVES: "GD_GET_SHARED_DRIVES",
	SET_SEARCH_TERM: "GD_SET_SEARCH_TERM",
	CLEAR_SEARCH_TERM: "GD_CLEAR_SEARCH_TERM",
	GET_FILES_STATUS: generateApiActionType("GD_GET_FILES_STATUS"),
	LOAD_TREE_FOLDERS: generateApiActionType("GD_LOAD_TREE_FOLDERS"),
	LOAD_NAVIGATION_FOLDERS: generateApiActionType("GD_LOAD_NAVIGATION_FOLDERS"),
	LOAD_NAVIGATION_FILES: generateApiActionType("GD_LOAD_NAVIGATION_FILES"),
	LOAD_SEARCH_FOLDERS: generateApiActionType("GD_LOAD_SEARCH_FOLDERS"),
	LOAD_SEARCH_FILES: generateApiActionType("GD_LOAD_SEARCH_FILES"),
	RESET_NAVIGATION: generateApiActionType("GD_RESET_NAVIGATION"),
	LOAD_SEARCH_ITEMS: generateApiActionType("GD_LOAD_SEARCH"),
	SET_NAVIGATION_CURRENT_FOLDER: "GD_SET_NAVIGATION_CURRENT_FOLDER",
	SET_SORT: "GD_SET_SORT",
	SET_ORDER_BY: "GD_SET_ORDER_BY",
	SET_BREADCRUMBS_PATH: "GD_SET_BREADCRUMBS_PATH",
	UPLOAD_FILES: "GD_UPLOAD_FILES",
	SET_FETCHING_FILE_DATA: "GD_SET_FETCHING_FILE_DATA",
	SET_WAITING_SIGN_IN: "GD_SET_WAITING_SIGN_IN",
	SET_USER_START_SIGN_IN: "GD_SET_USER_START_SIGN_IN",
	CLEAR_TAB_STATE: "GD_CLEAR_TAB_STATE",
};

const setAuthDetails = async (options: ServiceOptions, dispatch: Function, getState: Function, dispatcher: Function) => {
	const notifyNotAlreadySignedIn = (notLoggedIn) => {
		// called when user isnt already signed in, no need to wait anymore
		dispatcher(types.SET_WAITING_SIGN_IN, { waiting: false, notLoggedIn });
	};

	dispatcher([
		[types.SET_WAITING_SIGN_IN, { waiting: true }],
		[types.SET_AUTH_START],
	]);

	const authInfo = await getGDrive(options).initialize(notifyNotAlreadySignedIn);
	dispatcher(types.SET_AUTH_INFO, { authInfo });
};

const selectionActionTypes = { ...rootTypes, ...types };

export default createBoundActionCreators(types, {
	[types.CLEAR_IMAGE_SELECTION]: createCustomCreator(
		(context: string, dispatch: Function) => {
			createDispatcher(dispatch)([
				[types.CLEAR_IMAGE_SELECTION, { context }],
				[rootTypes.CLEAR_MAX_SELECT_REACHED],
			]);
		},
	),

	[types.INIT_AUTH]: createCustomCreator(setAuthDetails),

	[types.SET_FILE_SELECTION]: createCustomCreator(
		(
			{
				id,
				e,
				multiple,
			}: { id: string, e: MouseEvent, multiple: boolean },
			dispatch: Function,
			getState: Function,
		) => {
			const context = selectCurrentContext(getState());

			fileSelect(
				{
					id,
					e,
					types: selectionActionTypes,
					rootBindActions,
					selectIsIdSelected,
					selectReachedMaxSelection,
					selectMultipleIdsInRange,
					selectAvailableUntilMax,
					selectionParams: [context],
					multiple,
				},
				dispatch,
				getState,
			);
		},
	),

	[types.DO_LOGOUT]: createCustomCreator((dispatch: Function) => {
		const dispatcher = createDispatcher(dispatch);

		try {
			logger.log("[gdriveTab.actions]: about to log out drive user");
			getGDrive().logout();
			dispatcher(types.SET_USER_LOGOUT_SUCCESS);
		} catch (err) {
			logger.error(
				"[gdriveTab.actions]: failed to log out drive user",
				err,
			);
		}
	}),

	[types.DO_LOGIN]: createCustomCreator(
		(options: ServiceOptions, dispatch: Function, getState: Function, dispatcher: Function) => {
			try {
				logger.log("[gdriveTab.actions]: about to log in google user");

				dispatcher(types.SET_USER_START_SIGN_IN);

				const loginResult = getGDrive().login();

				if (loginResult) {
					loginResult.then(({ success }) => {
						if (!success) {
							// in case user closed login window without signing in
							dispatcher(types.SET_WAITING_SIGN_IN, { waiting: false, notLoggedIn: true });
						}
					});
				}

				setAuthDetails(options, dispatch, getState, dispatcher);
			} catch (err) {
				logger.error("[gdriveTab.actions]: failed to log in user", err);
			}
		},
	),

	[types.LOAD_TREE_FOLDERS]: createCustomCreator(
		({ id, ...params } = {}, dispatch: Function) => {
			getFiles(
				types.LOAD_TREE_FOLDERS,
				{
					...params,
					pageSize: driveConf.treePageSize,
					parentId: id,
					orderBy: ORDER_BY.NAME,
					sort: SORT.ASC,
					onlyFolders: true,
				},
				dispatch,
			);
		},
	),

	[types.LOAD_NAVIGATION_FOLDERS]: createCustomCreator(
		(
			{
				id, sort, orderBy, ...params
			} = {},
			dispatch: Function,
			getState: Function,
		) => {
			logger.log("[gdriveTab.actions]: retrieving navigation folders ", {
				id,
				params,
			});

			getFiles(
				types.LOAD_NAVIGATION_FOLDERS,
				{
					...params,
					parentId: id,
					onlyFolders: true,
					requestId: selectListRequestId(getState()),
					sort,
					orderBy,
				},
				dispatch,
			);
		},
	),

	[types.LOAD_NAVIGATION_FILES]: createCustomCreator(
		({
			id, sort, orderBy, ...params
		} = {}, dispatch: Function, getState: Function) => {
			getFiles(
				types.LOAD_NAVIGATION_FILES,
				{
					...params,
					parentId: id,
					requestId: selectListRequestId(getState()),
					sort,
					orderBy,
				},
				dispatch,
			);
		},
	),

	[types.LOAD_SEARCH_FOLDERS]: createCustomCreator(
		({
			term, sort, orderBy, cursor,
		} = {}, dispatch: Function, getState: Function) => {
			getFiles(
				types.LOAD_SEARCH_FOLDERS,
				{
					requestId: selectListRequestId(getState()),
					term,
					onlyFolders: true,
					sort,
					orderBy,
					cursor,
					path: [SEARCH_RESULTS_ID],
				},
				dispatch,
			);
		},
	),
	[types.LOAD_SEARCH_FILES]: createCustomCreator(
		({
			term, sort, orderBy, cursor,
		} = {}, dispatch: Function, getState: Function) => {
			getFiles(
				types.LOAD_SEARCH_FILES,
				{
					requestId: selectListRequestId(getState()),
					term,
					sort,
					orderBy,
					cursor,
				},
				dispatch,
			);
		},
	),

	[types.UPLOAD_FILES]: createCustomCreator(
		async ({ addToUploadQueue }, dispatch: Function, getState: Function, dispatcher: Function) => {
			const state = getState();
			const isCropEnabled = selectIsCropEnabled(state);
			const selectedIds = selectSelectedIds(state);
			const files = selectFiles(state);

			const selectedFiles = files.filter((f) => !!~selectedIds.indexOf(f.id));

			if (selectedFiles.length) {
				dispatcher(types.SET_FETCHING_FILE_DATA, { fetching: true });

				try {
					const generated = await getGDrive().generateFiles(selectedFiles, isCropEnabled);
					addToUploadQueue({ files: generated });
				} catch (ex) {
					logger.warn(
						"[gdriveTab]: encountered error when trying to upload files",
						ex.message,
					);

					addNotification(NOTIFICATIONS[NOTIFICATION_IDS.GDRIVE_RETRIEVE_FAILED], dispatch);
				} finally {
					dispatcher(types.SET_FETCHING_FILE_DATA, { fetching: false });
				}
			}
		},
	),
});
